import { useState, useEffect, useMemo } from 'react'
import {
  SessionPreferences as SessionPrefs,
  MealType,
  DEFAULT_SESSION_PREFERENCES,
  WORKWEEK_MEAL_COUNTS,
  CUISINE_TYPES,
  MEAL_TIMING_PREFERENCES
} from '@talktoast/shared'
import { createDebouncedSync, SyncStatus, clientLogger } from '@talktoast/shared'

interface SessionPreferencesProps {
  initialPreferences?: SessionPrefs | null
  /** When true, override saved meal counts with 5-day workweek defaults (used by "Plan Your Week" entry point) */
  forceWeeklyDefaults?: boolean
  onUpdate: (preferences: SessionPrefs) => void
  onSyncStatusChange?: (status: SyncStatus) => void
  onStart: () => void
  fromSettings?: boolean  // If true, show "Save & Back" instead of "Start Planning"
}

export default function SessionPreferences({
  initialPreferences,
  forceWeeklyDefaults = false,
  onUpdate,
  onSyncStatusChange,
  onStart,
  fromSettings = false
}: SessionPreferencesProps) {
  // When forceWeeklyDefaults is true, override saved meal counts with workweek defaults (5 days)
  const getInitialPreferences = (): SessionPrefs => {
    const base = initialPreferences || DEFAULT_SESSION_PREFERENCES
    if (forceWeeklyDefaults) {
      clientLogger.debug({}, 'Forcing 5-day workweek defaults (overriding saved meal counts)')
      return { ...base, mealCounts: { ...WORKWEEK_MEAL_COUNTS } }
    }
    return base
  }

  const [preferences, setPreferences] = useState<SessionPrefs>(getInitialPreferences)
  const [showCuisineOptions, setShowCuisineOptions] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ state: 'idle', lastSync: null, error: null })

  // Create debounced sync function
  const debouncedSync = useMemo(() => {
    return createDebouncedSync<SessionPrefs>(
      async (newPreferences) => {
        clientLogger.debug({}, 'Syncing preferences to backend')
        // Call the update function which should trigger backend sync
        await onUpdate(newPreferences)
      },
      500 // 500ms debounce
    )
  }, [onUpdate])

  // Listen to sync status changes
  useEffect(() => {
    const cleanup = debouncedSync.addListener((status) => {
      setSyncStatus(status)
      onSyncStatusChange?.(status)
    })
    return cleanup
  }, [debouncedSync, onSyncStatusChange])

  // Handle preference changes with optimistic updates
  const handlePreferenceChange = (newPreferences: SessionPrefs) => {
    clientLogger.debug({}, 'Preference changed, updating locally and syncing')
    // Update local state immediately for responsive UI
    setPreferences(newPreferences)
    // Trigger debounced backend sync (unless from settings - manual save only)
    if (!fromSettings) {
      debouncedSync.sync(newPreferences)
    }
  }

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)
  }, [])

  const updateMealType = (type: MealType['type'], enabled: boolean) => {
    const newPreferences = {
      ...preferences,
      mealTypes: preferences.mealTypes.map(meal =>
        meal.type === type ? { ...meal, enabled } : meal
      )
    }
    handlePreferenceChange(newPreferences)
  }

  const updateMealCount = (type: keyof typeof preferences.mealCounts, count: number) => {
    const newPreferences = {
      ...preferences,
      mealCounts: {
        ...preferences.mealCounts,
        [type]: Math.max(0, Math.min(14, count)) // Limit to 0-14
      }
    }
    handlePreferenceChange(newPreferences)
  }

  const getEnabledMealTypes = () => {
    return preferences.mealTypes.filter(meal => meal.enabled)
  }

  const getTotalMeals = () => {
    return preferences.mealTypes.reduce((total, meal) => {
      if (meal.enabled) {
        return total + preferences.mealCounts[meal.type]
      }
      return total
    }, 0)
  }

  const toggleCuisine = (cuisineValue: string) => {
    const newPreferences = {
      ...preferences,
      cuisinePreferences: preferences.cuisinePreferences.includes(cuisineValue)
        ? preferences.cuisinePreferences.filter(c => c !== cuisineValue)
        : [...preferences.cuisinePreferences, cuisineValue]
    }
    handlePreferenceChange(newPreferences)
  }

  return (
    <div className="flex flex-col gap-3 md:gap-4">
      {/* Weekly defaults banner — shown when launched from "Plan Your Week" */}
      {forceWeeklyDefaults && (
        <div className="px-3 py-2 bg-blue-50 border border-blue-200 border-l-4 border-l-blue-500 rounded-lg text-xs text-blue-900">
          Planning a workweek (5 days). You can adjust meal counts below.
        </div>
      )}

      {/* Compact Header with Remember Toggle */}
      <div className="text-center space-y-1">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <h2 className="text-xl font-bold text-gray-900">Let's plan your meals</h2>
          <button
            onClick={() => {
              const newPreferences = {
                ...preferences,
                rememberSettings: !preferences.rememberSettings
              }
              handlePreferenceChange(newPreferences)
            }}
            className={`
              px-2 py-1 rounded-full text-xs font-medium transition-all border
              ${preferences.rememberSettings
                ? 'bg-toast-gold text-white border-toast-gold'
                : 'bg-white text-gray-600 border-gray-300 hover:border-toast-gold'
              }
            `}
            title={preferences.rememberSettings ? 'Settings will be remembered' : 'Settings will not be remembered'}
          >
            {preferences.rememberSettings ? '💾 Saved' : 'Save for next time?'}
          </button>
        </div>
        <p className="text-xs text-gray-500">Tell us what you need for this week</p>
      </div>

      {/* Number of People - Inline */}
      <div className="flex items-center justify-between px-1">
        <label className="text-sm font-medium text-gray-700">
          Cooking for
        </label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const newPreferences = {
                ...preferences,
                numberOfPeople: Math.max(1, preferences.numberOfPeople - 1)
              }
              handlePreferenceChange(newPreferences)
            }}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-gray-300 hover:border-toast-gold text-gray-700 hover:text-toast-gold transition-all flex items-center justify-center"
          >
            −
          </button>
          <span className="text-lg font-semibold text-gray-900 min-w-[2.5rem] text-center">
            {preferences.numberOfPeople}
          </span>
          <button
            onClick={() => {
              const newPreferences = {
                ...preferences,
                numberOfPeople: Math.min(12, preferences.numberOfPeople + 1)
              }
              handlePreferenceChange(newPreferences)
            }}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-gray-300 hover:border-toast-gold text-gray-700 hover:text-toast-gold transition-all flex items-center justify-center"
          >
            +
          </button>
        </div>
      </div>

      {/* Meal Timing Preference - Segmented Pills */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 px-1">Cooking time preference</label>
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-gray-100 rounded-lg">
          {MEAL_TIMING_PREFERENCES.map(timing => (
            <button
              key={timing.value}
              onClick={() => {
                const newPreferences = {
                  ...preferences,
                  mealTimingPreference: timing.value
                }
                handlePreferenceChange(newPreferences)
              }}
              className={`px-2 py-2 rounded-md text-xs font-medium transition-all ${
                preferences.mealTimingPreference === timing.value
                  ? 'bg-toast-gold text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title={timing.description}
            >
              {timing.label}
            </button>
          ))}
        </div>
      </div>

      {/* Meal Types - Badge Pills */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 px-1">Meals to plan</label>

        {/* Badge Pills */}
        <div className="flex flex-wrap gap-2">
          {preferences.mealTypes.map(meal => {
            const mealEmoji = { breakfast: '🍳', lunch: '🥗', dinner: '🍽️', snacks: '🍿', dessert: '🍰' }[meal.type] || '🍽️'
            return (
              <button
                key={meal.type}
                onClick={() => updateMealType(meal.type, !meal.enabled)}
                className={`
                  px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2
                  flex items-center gap-1.5
                  ${meal.enabled
                    ? 'bg-toast-gold text-white border-toast-gold'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                  }
                `}
              >
                <span className="text-base">{mealEmoji}</span>
                <span className="capitalize">{meal.type}</span>
                {meal.enabled && (
                  <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                    {preferences.mealCounts[meal.type]}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Expanded Controls - Compact Layout */}
        {preferences.mealTypes.filter(m => m.enabled).length > 0 && (
          <div className="space-y-1.5 p-2.5 md:p-3 bg-gray-50 rounded-lg">
            {preferences.mealTypes.filter(m => m.enabled).map(meal => {
              const mealEmoji = { breakfast: '🍳', lunch: '🥗', dinner: '🍽️', snacks: '🍿', dessert: '🍰' }[meal.type] || '🍽️'
              return (
                <div key={meal.type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{mealEmoji}</span>
                    <span className="text-sm font-medium capitalize">{meal.type}</span>
                  </div>

                  {/* Compact counter */}
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <button
                      onClick={() => updateMealCount(meal.type, preferences.mealCounts[meal.type] - 1)}
                      className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-gray-300 hover:border-toast-gold text-gray-600 hover:text-toast-gold transition-all flex items-center justify-center text-sm"
                    >
                      −
                    </button>
                    <span className="text-sm font-semibold min-w-[1.5rem] text-center">
                      {preferences.mealCounts[meal.type]}
                    </span>
                    <button
                      onClick={() => updateMealCount(meal.type, preferences.mealCounts[meal.type] + 1)}
                      className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-gray-300 hover:border-toast-gold text-gray-600 hover:text-toast-gold transition-all flex items-center justify-center text-sm"
                    >
                      +
                    </button>
                    <span className="text-xs text-gray-500 whitespace-nowrap">/wk</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Cuisine Preferences - Refined Accordion */}
      <div className="space-y-2">
        <button
          onClick={() => setShowCuisineOptions(!showCuisineOptions)}
          className="w-full flex items-center justify-between text-left px-1 group"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Cuisine preferences</span>
            {preferences.cuisinePreferences.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-toast-gold text-white">
                {preferences.cuisinePreferences.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500 group-hover:text-toast-gold">
            <span>{showCuisineOptions ? 'Hide' : 'Show'}</span>
            <svg
              className={`w-4 h-4 transition-transform ${showCuisineOptions ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {showCuisineOptions && (
          <div className="p-2.5 md:p-3 bg-gray-50 rounded-lg space-y-2.5">
            <p className="text-xs text-gray-600">
              Leave empty for variety, or select favorites
            </p>

            {/* Cuisine Pills */}
            <div className="flex flex-wrap gap-2">
              {CUISINE_TYPES.map(cuisine => (
                <button
                  key={cuisine.value}
                  onClick={() => toggleCuisine(cuisine.value)}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2
                    flex items-center gap-1.5
                    ${preferences.cuisinePreferences.includes(cuisine.value)
                      ? 'bg-toast-gold text-white border-toast-gold'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                    }
                  `}
                >
                  <span>{cuisine.emoji}</span>
                  <span>{cuisine.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sync Status Indicator - Compact Badge */}
      {syncStatus.state !== 'idle' && (
        <div className="flex justify-center">
          <span className={`
            px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5
            ${syncStatus.state === 'syncing' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}
          `}>
            {syncStatus.state === 'syncing' && (
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {syncStatus.state === 'syncing' ? 'Saving...' : 'Save failed'}
          </span>
        </div>
      )}

      {/* Start Planning Button - Toast Gold Theme */}
      <button
        onClick={async () => {
          // If from settings, manually save before navigating back
          if (fromSettings) {
            await onUpdate(preferences)
          }
          onStart()
        }}
        disabled={getEnabledMealTypes().length === 0 || syncStatus.state === 'syncing'}
        className={`
          w-full py-3 px-6 rounded-lg font-semibold text-white transition-all
          ${getEnabledMealTypes().length === 0 || syncStatus.state === 'syncing'
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-toast-gold hover:brightness-110 shadow-md hover:shadow-lg'
          }
        `}
      >
        {syncStatus.state === 'syncing' ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        ) : getEnabledMealTypes().length === 0 ? (
          'Select at least one meal'
        ) : fromSettings ? (
          'Save & Back'
        ) : (
          'Start Planning'
        )}
      </button>
    </div>
  )
}